import { type AbsenceType, CommonErrors } from '@lmc/ts-common'
import { Form } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { handleSubmit } from 'screens/Rostering/formComponents/utils'
import type { AbsenceTypesFormType } from 'screens/Rostering/hooks/useAbsenceTypesList'
import { PageWrapper } from '../../components'
import AbsenceTypesFormList from './AbsenceTypesFormList'
import FixedFooter from './FixedFooter'

type AbsenceTypesFormProps = {
  absenceTypes: AbsenceType[]
  isAbsenceTypesLoading: boolean
  updateAbsenceTypes: (obj: { types: AbsenceType[] }) => void
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
}

const AbsenceTypesForm = ({
  absenceTypes,
  isAbsenceTypesLoading,
  updateAbsenceTypes,
  isDirty,
  setIsDirty
}: AbsenceTypesFormProps) => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const handleOnFinish = async (values: AbsenceTypesFormType) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      updateAbsenceTypes({ types: values.absenceTypes })
      setIsUpdateLoading(false)
    } catch (err: unknown) {
      const error = err as { code?: number; message?: string } | undefined
      const errorCode = error?.code

      if (errorCode === CommonErrors.Codes.rotaSettingsTypeInUse) {
        showToast({
          title: t(`YOU_CANNOT_DELETE_THE_ITEM_WHEN_IT_IS_IN_USE`),
          type: 'error'
        })

        const newAbsenceValue: AbsenceTypesFormType['absenceTypes'] =
          values.absenceTypes.map((value) => ({
            ...value,
            isDeleted: false
          }))

        form.setFieldValue('absenceTypes', newAbsenceValue)
      } else {
        showToast({
          title: error?.message || t('SETTINGS_ERROR'),
          type: 'error'
        })
      }
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const handleFormValuesChange = (values: AbsenceTypesFormType) => {
    if (!values?.absenceTypes) {
      return
    }

    const getCurrentValues = (items: AbsenceType[]) =>
      items
        .filter((item) => item && !item.isDeleted)
        .map(({ name, abbreviation }) => ({ name, abbreviation }))

    const currentValues = getCurrentValues(values.absenceTypes)
    const initialValues = getCurrentValues(absenceTypes || [])

    const isDifferent =
      JSON.stringify(currentValues) !== JSON.stringify(initialValues)
    setIsDirty(isDifferent)
  }

  const getDeleteFieldKey = (fieldName: number) => [
    'absenceTypes',
    fieldName,
    'isDeleted'
  ]

  const deleteHandler = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    form.setFieldValue(deleteFieldName, true)
    const values = form.getFieldsValue()
    handleFormValuesChange(values)
  }

  const filterDeletedFields = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    const isDeleted = form.getFieldValue(deleteFieldName)
    return isDeleted !== true
  }

  const initialValues = absenceTypes
    ? {
        absenceTypes: absenceTypes.filter((absence) => !absence.isDeleted)
      }
    : {}

  return (
    <PageWrapper>
      <Form<AbsenceTypesFormType>
        form={form}
        onFinish={handleOnFinish}
        onValuesChange={handleFormValuesChange}
        initialValues={initialValues}
        validateTrigger={['']}
      >
        {isAbsenceTypesLoading ? (
          <Spinner />
        ) : (
          <AbsenceTypesFormList
            filterDeletedFields={filterDeletedFields}
            deleteHandler={deleteHandler}
            form={form}
          />
        )}
        <FixedFooter
          isUpdateLoading={isUpdateLoading}
          isDirty={isDirty}
          handleSubmit={() => handleSubmit(form, handleOnFinish)}
        />
      </Form>
    </PageWrapper>
  )
}

export default AbsenceTypesForm
